<template>
  <v-card
    outlined
    class="mb-3 px-3 py-2"
  >
    <div class="d-flex align-center">
      <span class="font-weight-bold">Selected Employees</span>
      <span class="ml-2 grey--text text--darken-1">{{ counterText }}</span>
      <v-spacer />
      <v-btn
        small
        text
        color="error"
        :disabled="!value.length"
        @click="$emit('input', [])"
      >
        Clear All
      </v-btn>
    </div>

    <div
      class="pt-1 selected-employees__chips"
      :style="{ maxHeight: `${rows * chipRowHeight + 4}px` }"
    >
      <span
        v-if="!value.length"
        class="caption grey--text"
      >
        No employees selected. Search and tick employees in the table below to add them.
      </span>
      <v-chip
        v-for="employee in visibleEmployees"
        ref="chip"
        :key="employee.employee_uid"
        small
        close
        class="mr-1 mb-1"
        @click:close="remove(employee)"
      >
        {{ employeeName(employee) }}
      </v-chip>
      <!-- a scrollable menu rather than a tooltip, so a long list can't stretch the page -->
      <v-menu
        v-if="hiddenEmployees.length"
        offset-y
        max-width="400"
      >
        <template v-slot:activator="{ on }">
          <v-chip
            ref="more"
            small
            outlined
            class="mb-1"
            v-on="on"
          >
            +{{ hiddenEmployees.length }} more
          </v-chip>
        </template>
        <v-card>
          <div class="px-3 pt-2 pb-1 caption grey--text text--darken-1">
            {{ hiddenEmployees.length }} more selected
          </div>
          <div class="selected-employees__more-list pb-2">
            <div
              v-for="employee in hiddenEmployees"
              :key="employee.employee_uid"
              class="d-flex align-center px-3 py-1 body-2"
            >
              <span class="text-truncate flex-grow-1 mr-2">{{ employeeName(employee) }}</span>
              <v-btn
                icon
                x-small
                @click.stop="remove(employee)"
              >
                <v-icon small>
                  mdi-close
                </v-icon>
              </v-btn>
            </div>
          </div>
        </v-card>
      </v-menu>
    </div>

    <v-dialog
      :value="pendingRemoval.length > 0"
      persistent
      max-width="500"
    >
      <v-card>
        <v-card-title class="headline">
          Remove selected employees?
        </v-card-title>
        <v-card-text>
          <p>
            The following {{ pendingRemoval.length === 1 ? 'employee is' : pendingRemoval.length + ' employees are' }}
            not in the applied filter and will be removed from the selection:
          </p>
          <ul class="selected-employees__removal-list">
            <li
              v-for="employee in pendingRemoval"
              :key="employee.employee_uid"
            >
              {{ employeeName(employee) }}
            </li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="$emit('cancel-removal')"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            text
            @click="$emit('confirm-removal')"
          >
            Remove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
  export default {
    props: {
      value: {
        type: Array,
        required: true,
      },
      pendingRemoval: {
        type: Array,
        default: () => [],
      },
      rows: {
        type: Number,
        default: 2,
      },
    },
    data () {
      return {
        // small chip (24px) + mb-1 (4px)
        chipRowHeight: 28,
        // never render more chips than could possibly fit, however many are selected
        maxRenderedChips: 40,
        visibleCount: 8,
        fitRun: 0,
      }
    },
    computed: {
      counterText () {
        return `${this.value.length} ${this.value.length === 1 ? 'Employee' : 'Employees'} Selected`
      },
      visibleEmployees () {
        return this.value.slice(0, this.visibleCount)
      },
      hiddenEmployees () {
        return this.value.slice(this.visibleCount)
      },
    },
    watch: {
      value () {
        this.fitChips()
      },
    },
    mounted () {
      this.onResize = _.debounce(() => this.fitChips(), 150)
      window.addEventListener('resize', this.onResize)
      this.fitChips()
    },
    beforeDestroy () {
      window.removeEventListener('resize', this.onResize)
      this.onResize.cancel()
    },
    methods: {
      /**
       * Show as many chips as fit in `rows` rows, keeping room for the "+n more" chip.
       * Renders a capped batch, measures where each chip wrapped to, then trims.
       */
      fitChips () {
        const run = ++this.fitRun

        this.visibleCount = Math.min(this.value.length, this.maxRenderedChips)

        this.$nextTick(() => {
          const chips = (this.$refs.chip || []).map(chip => chip.$el)

          // hidden (e.g. inactive tab) - nothing to measure, keep the batch as is
          if (run !== this.fitRun || !chips.length || !chips[0].offsetHeight) return

          const firstTop = chips[0].offsetTop
          const fitsInRows = el => el.offsetTop - firstTop < this.chipRowHeight * (this.rows - 0.5)

          let count = 0
          while (count < chips.length && fitsInRows(chips[count])) count++
          this.visibleCount = count

          // drop chips one by one until "+n more" also fits
          const makeRoomForMore = () => {
            if (this.visibleCount === 0 || this.visibleCount >= this.value.length) return

            this.$nextTick(() => {
              if (run !== this.fitRun) return

              const more = this.$refs.more && this.$refs.more.$el
              if (!more || fitsInRows(more)) return

              this.visibleCount--
              makeRoomForMore()
            })
          }

          makeRoomForMore()
        })
      },
      employeeName (employee) {
        return (employee.person || {}).name || '-'
      },
      remove (employee) {
        this.$emit('input', this.value.filter(selected => selected.employee_uid !== employee.employee_uid))
      },
    },
  }
</script>

<style scoped>
  .selected-employees__removal-list {
    max-height: 240px;
    overflow-y: auto;
  }

  .selected-employees__chips {
    overflow: hidden;
  }

  .selected-employees__more-list {
    max-height: 300px;
    overflow-y: auto;
  }
</style>
